import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Media,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faExclamationTriangle, faTruck } from '@fortawesome/free-solid-svg-icons'
import FieldError from '../provider/FieldError'
import ServicePhotosField from '../provider/services/ServicePhotosField'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import { createServiceRequest } from '../../../helpers/api/marketplace'
import { MAX_PHOTO_MB, MAX_REQUEST_PHOTOS, formatAvailability, formatVisitFee } from '../marketplaceConstants'

const DESCRIPTION_MAX = 2000
const ADDRESS_MAX = 250

const Required = () => <span className="text-danger ml-1">*</span>

const emptyForm = (service) => ({
  description: '',
  address: service?.default_address || '',
  suggested_date: '',
  suggested_time: ''
})

const validate = (form, today) => {
  const errors = {}
  if (form.description.trim().length < 10) errors.description = 'Cuéntale al proveedor qué pasa: al menos 10 caracteres.'
  if (!form.address.trim()) errors.address = 'Indica la dirección de la visita.'
  if (!form.suggested_date) errors.suggested_date = 'Elige una fecha.'
  else if (form.suggested_date < today) errors.suggested_date = 'La fecha sugerida no puede ser anterior a hoy.'
  if (!form.suggested_time) errors.suggested_time = 'Elige una hora.'
  return errors
}

const isOutsideSchedule = (service, date, time) => {
  if (date && service.available_days?.length && !service.available_days.includes(moment(date).isoWeekday())) return true
  if (time && service.available_from && service.available_to) {
    return time < service.available_from || time > service.available_to
  }
  return false
}

const normalizeServerErrors = (serverErrors) =>
  Object.entries(serverErrors).reduce((acc, [field, messages]) => {
    const key = field.split('.')[0]
    if (!acc[key]) acc[key] = [].concat(messages)[0]
    return acc
  }, {})

const ServiceRequestModal = ({ isOpen, toggle, service, onSent }) => {
  const [form, setForm] = useState(() => emptyForm(service))
  const [photos, setPhotos] = useState([])
  const [errors, setErrors] = useState({})
  const [sending, setSending] = useState(false)
  const objectUrls = useRef(new Set())
  const today = moment().format('YYYY-MM-DD')

  useEffect(() => {
    const urls = objectUrls.current
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev
      const { [field]: removed, ...rest } = prev
      return rest
    })

  const handleChange = (field) => ({ target }) => {
    setForm((prev) => ({ ...prev, [field]: target.value }))
    clearError(field)
  }

  const addPhotos = (files) => {
    const added = files.map((file, index) => {
      const url = URL.createObjectURL(file)
      objectUrls.current.add(url)
      return { key: `new-${Date.now()}-${index}-${file.name}`, file, url }
    })
    setPhotos((prev) => [...prev, ...added])
    clearError('photos')
  }

  const removePhoto = (key) =>
    setPhotos((prev) => {
      const removed = prev.find((photo) => photo.key === key)
      if (removed) {
        URL.revokeObjectURL(removed.url)
        objectUrls.current.delete(removed.url)
      }
      return prev.filter((photo) => photo.key !== key)
    })

  const handleSubmit = async (event) => {
    event.preventDefault()
    const formErrors = validate(form, today)
    if (Object.keys(formErrors).length) {
      setErrors(formErrors)
      return
    }

    setSending(true)
    try {
      const { data } = await createServiceRequest({
        service_id: service.id,
        description: form.description.trim(),
        address: form.address.trim(),
        suggested_date: form.suggested_date,
        suggested_time: form.suggested_time,
        photos: photos.map((photo) => photo.file)
      })
      Toast.fire({ type: 'success', title: 'Solicitud enviada.' })
      onSent(data)
    } catch (error) {
      const serverErrors = error?.data?.errors
      if (serverErrors) {
        setErrors(normalizeServerErrors(serverErrors))
      } else {
        handleErrorMessage(error?.data || error)
      }
    } finally {
      setSending(false)
    }
  }

  if (!service) return null

  const schedule = formatAvailability(service.available_days, service.available_from, service.available_to)

  return (
    <Modal isOpen={isOpen} toggle={sending ? undefined : toggle} size="lg" centered>
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader toggle={sending ? undefined : toggle}>Solicitar visita técnica / Cotización</ModalHeader>
        <ModalBody>
          <Media className="align-items-center bg-light rounded p-3 mb-3">
            <Media body>
              <h6 className="mb-1">{service.title}</h6>
              <p className="fs--1 text-700 mb-0">
                {service.provider?.display_name}
                <span className="mx-2 text-400">|</span>
                <FontAwesomeIcon icon={faTruck} className="text-500 mr-1" />
                {formatVisitFee(service)}
              </p>
            </Media>
          </Media>

          {errors.service_id && <p className="text-danger fs--1">{errors.service_id}</p>}

          <FormGroup>
            <Label for="request-description">
              Detalle del problema o requerimiento
              <Required />
            </Label>
            <Input
              id="request-description"
              type="textarea"
              rows={4}
              maxLength={DESCRIPTION_MAX}
              value={form.description}
              invalid={Boolean(errors.description)}
              onChange={handleChange('description')}
              placeholder="Ej.: Se sale el agua por debajo del lavamanos del baño principal desde hace dos días."
            />
            <div className="d-flex">
              <FieldError error={errors.description} />
              <small className="text-500 ml-auto mt-1">
                {form.description.length}/{DESCRIPTION_MAX}
              </small>
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Fotos de la falla</Label>
            <ServicePhotosField
              photos={photos}
              onAdd={addPhotos}
              onRemove={removePhoto}
              error={errors.photos}
              max={MAX_REQUEST_PHOTOS}
              showCover={false}
              hint={`Opcional. Hasta ${MAX_REQUEST_PHOTOS} fotos para que el proveedor vea la falla. JPG, PNG o WEBP de hasta ${MAX_PHOTO_MB} MB.`}
            />
          </FormGroup>

          <FormGroup>
            <Label for="request-address">
              Dirección de la visita
              <Required />
            </Label>
            <Input
              id="request-address"
              maxLength={ADDRESS_MAX}
              value={form.address}
              invalid={Boolean(errors.address)}
              onChange={handleChange('address')}
            />
            <small className="text-600 d-block mt-1">Por defecto, la de tu inmueble. Puedes cambiarla.</small>
            <FieldError error={errors.address} />
          </FormGroup>

          <Row form>
            <Col sm={6}>
              <FormGroup className="mb-sm-0">
                <Label for="request-date">
                  Fecha sugerida
                  <Required />
                </Label>
                <Input
                  id="request-date"
                  type="date"
                  min={today}
                  value={form.suggested_date}
                  invalid={Boolean(errors.suggested_date)}
                  onChange={handleChange('suggested_date')}
                />
                <FieldError error={errors.suggested_date} />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup className="mb-0">
                <Label for="request-time">
                  Hora sugerida
                  <Required />
                </Label>
                <Input
                  id="request-time"
                  type="time"
                  value={form.suggested_time}
                  invalid={Boolean(errors.suggested_time)}
                  onChange={handleChange('suggested_time')}
                />
                <FieldError error={errors.suggested_time} />
              </FormGroup>
            </Col>
          </Row>
          {service.available_days?.length > 0 &&
            (isOutsideSchedule(service, form.suggested_date, form.suggested_time) ? (
              <Alert color="warning" className="fs--1 mt-2 mb-0 py-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                Está fuera del horario del proveedor ({schedule}); es posible que te proponga otro día u hora.
              </Alert>
            ) : (
              <small className="text-600 d-block mt-2">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                Horario del proveedor: {schedule}
              </small>
            ))}
        </ModalBody>
        <ModalFooter>
          <Button color="falcon-default" onClick={toggle} disabled={sending}>
            Cancelar
          </Button>
          <Button type="submit" color="primary" disabled={sending}>
            {sending && <Spinner size="sm" className="mr-2" />}
            Enviar solicitud
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default ServiceRequestModal
